import {
  Component,
  Inject,
  NgZone,
  PLATFORM_ID,
  Input,
  OnDestroy,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

// amCharts imports
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

@Component({
  selector: 'tools-usage-chart',
  templateUrl: './tools-usage-chart.component.html',
  styleUrls: ['./tools-usage-chart.component.css'],
  standalone: false,
})
export class ToolsUsageChartComponent implements OnDestroy {
  @Input() toolsAccessedHours!: { tool_name: string; time_spent: number }[];

  private root!: am5.Root;

  // chart variable
  private chart!: am5xy.XYChart;

  // data series variable
  private series1!: am5xy.ColumnSeries; //student university hours fall semester

  // label variable
  private labelSubtitle!: am5.Label;
  private labelTitle!: am5.Label;
  private xAxisLabel!: am5.Label;

  // Define variable for chart data
  private toolsUsageData: { tool_name: string; time_spent: number }[] = [
    { tool_name: 'Hammer', time_spent: 10 },
    { tool_name: 'Screwdriver', time_spent: 135 },
    { tool_name: 'Wrench', time_spent: 230 },
  ];

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
  ) {}

  ngOnInit(): void {
    this.root = am5.Root.new('toolsUsageChartDiv');
    this.generateGraph();
  }

  ngOnChanges() {
    this.generateGraph();
  }

  ngAfterViewInit() {}

  // called once the component is destroyed
  ngOnDestroy() {
    // Clean up chart when the component is removed
    this.browserOnly(() => {
      if (this.root) {
        this.root.dispose();
      }
    });
  }

  generateGraph() {
    this.root.setThemes([am5themes_Animated.new(this.root)]);

    // remove previous chart in root
    if (this.chart) {
      this.root.container.children.pop();
    }

    this.chart = this.root.container.children.push(
      am5xy.XYChart.new(this.root, {
        layout: this.root.verticalLayout,
      }),
    );

    let xRenderer = am5xy.AxisRendererX.new(this.root, { minGridDistance: 30 });
    xRenderer.labels.template.setAll({
      rotation: -45,
      centerY: am5.p50,
      centerX: am5.p100,
      paddingRight: 15,
    });

    // Create axes
    let xAxis = this.chart.xAxes.push(
      am5xy.CategoryAxis.new(this.root, {
        categoryField: 'tool_name',
        // renderer: am5xy.AxisRendererX.new(this.root, {}),
        renderer: xRenderer,
        tooltip: am5.Tooltip.new(this.root, {}),
      }),
    );
    xAxis.data.setAll(this.toolsUsageData);

    let yAxis = this.chart.yAxes.push(
      am5xy.ValueAxis.new(this.root, {
        renderer: am5xy.AxisRendererY.new(this.root, {}),
      }),
    );

    if (this.series1) {
      this.chart.series.pop();
    }

    // Add series
    this.series1 = this.chart.series.push(
      am5xy.ColumnSeries.new(this.root, {
        name: 'Hours',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'time_spent',
        categoryXField: 'tool_name',
      }),
    );
    this.series1.data.setAll(this.toolsUsageData);

    //Set tooltip data
    this.series1.columns.template.setAll({
      tooltipText: '{name} in {categoryX}: {valueY}',
      tooltipY: am5.percent(10),
      templateField: 'columnSettings',
    });

    // Set cursor
    this.chart.set('cursor', am5xy.XYCursor.new(this.root, {}));

    // Add legend
    let legend = this.chart.children.push(am5.Legend.new(this.root, {}));
    legend.data.setAll(this.chart.series.values);

    // Make stuff animate on load
    this.chart.appear(1000, 100);
    this.series1.appear();

    // Add label in Y-axis
    yAxis.axisHeader.children.push(
      am5.Label.new(this.root, {
        text: 'Hours',
        fontFamily: "'Gotham SSm A','Gotham SSm B', 'Arial', 'sans-serif'",
        fontSize: 20,
        fontWeight: '500',
      }),
    );

    // Add titles
    // Title Label
    // this.labelTitle = this.chart.children.unshift(
    //   am5.Label.new(this.root, {
    //     text: '',
    //     fontSize: 20,
    //     fontFamily: "'Arial', 'Helvetica', 'sans-serif'",
    //     fontWeight: '900',
    //     textAlign: 'center',
    //     x: am5.percent(50),
    //     y: am5.percent(0),
    //   })
    // );

    // Subtitle Label
    // this.labelSubtitle = this.chart.children.unshift(
    //   am5.Label.new(this.root, {
    //     text: '',
    //     fontSize: 14,
    //     fontFamily: "'Gotham SSm A','Gotham SSm B', 'Arial', 'sans-serif'",
    //     textAlign: 'center',
    //     x: am5.percent(50),
    //     y: am5.percent(6),
    //   })
    // );

    // Add label in X-axis
    this.xAxisLabel = this.chart.children.unshift(
      am5.Label.new(this.root, {
        text: '',
        fontSize: 20,
        fontFamily: "'Gotham SSm A','Gotham SSm B', 'Arial', 'sans-serif'",
        fontWeight: '500',
        textAlign: 'center',
        x: am5.percent(50),
        centerX: am5.percent(50),
        y: am5.percent(100),
        centerY: am5.percent(100),
        paddingTop: 0,
        paddingBottom: 0,
      }),
    );

    // Changing label contents
    this.xAxisLabel.set('text', 'Tools');
  }

  // reset data in series for incoming new data
  resetSeriesData() {
    // this.generateGraph();
  }

  // Run the function only in the browser
  browserOnly(f: () => void) {
    if (isPlatformBrowser(this.platformId)) {
      this.zone.runOutsideAngular(() => {
        f();
      });
    }
  }
}
